import { Fragment, useRef, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';

import logo from '../public/img/logo.svg';
import menu from '../public/img/menu.svg';
import language from '../languages/Navbar.json';
import ContactUsCTA from './ContactUsCTA';

const Navbar = ({ locale }) => {
  const router = useRouter();
  const sideNav = useRef();
  const { asPath } = router;

  const navMenu = [
    {
      name: { en: 'Home', es: 'Hogar', pt: 'Início' },
      type: 'link',
      link: '/',
    },
    {
      name: { en: 'Product', es: 'Productos', pt: 'Produtos' },
      type: 'dropdown',
      items: [
        {
          name: {
            en: 'Damage Detection',
            es: 'Detección de daños',
            pt: 'Detecção de danos',
          },
          link: '/damage-detection',
          title: 'Damage detection software'
        },
        {
          name: {
            en: 'Claim Assessment',
            es: 'Evaluación de reclamos',
            pt: 'Avaliação de sinistros',
          },
          link: '/claim-estimation',
          title: 'AI-powered claim estimation '
        },
        {
          name: {
            en: 'Fraud Detection',
            es: 'Detección de fraude',
            pt: 'Detecção de fraude',
          },
          link: '/fraud-detection',
          title: 'Real-time vehicle insurance fraud detection'
        },
        {
          name: {
            en: 'Photo & Video Capture',
            es: 'Captura de fotografía y vídeo',
            pt: 'Captura de foto e vídeo',
          },
          link: '/photo-video-capture',
          title: 'Photo Video Capture'
        },
        {
          name: {
            en: 'Photo Quality API',
          },
          link: '/photo-quality',
          title: 'Photo Quality API'
        },
        {
          name: {
            en: 'Vehicle Damage Scanners',
          },
          link: '/vehicle-damage-scanners',
          title: 'Vehicle Damage Scanners'
        },
        {
          name: {
            en: 'AI-powered OCR',
          },
          link: '/ai-powered-ocr',
          title: 'AI-powered OCR'
        }
      ],
    },
    {
      name: { en: 'Solutions', es: 'Soluciones', pt: 'Soluções' },
      type: 'dropdown',
      items: [
        {
          name: { en: 'Automotive', es: 'Automotor', pt: 'Automotivo' },
          link: '/automotive',
          title: 'Automotive'
        },
        {
          name: { en: 'Insurance', es: 'Seguro', pt: 'Seguros' },
          link: '/insurance',
          title: 'AI vehicle inspection for insurance claims'
        },
        {
          name: {
            en: 'Car Rental/Leasing',
          },
          link: '/car-rental',
          title: 'Car Rental Inspection Software'
        },
        {
          name: {
            en: 'Vehicle Remarketing',
          },
          link: '/vehicle-remarketing',
          title: 'Vehicle Remarketing'
        },
        {
          name: {
            en: 'Fleet Management',
          },
          link: '/fleet-inspection-software',
          title: 'Fleet Management'
        },
        {
          name: {
            en: 'Truck Inspection Software',
          },
          link: '/truck-inspection-software',
          title: 'Truck Inspection Software'
        },
        {
          name: {
            en: 'Motorcycle Inspection Software',
          },
          link: '/motorcycle-inspection-software',
          title: 'Motorcycle Inspection Software'
        }
      ],
    },
    {
      name: { en: 'Resources', es: 'Recursos', pt: 'Recursos' },
      type: 'dropdown',
      items: [
        { name: { en: 'Media', es: 'Medios', pt: 'Mídia' }, link: '/media' },
        { name: { en: 'Blog', es: 'Blog', pt: 'Blog' }, link: '/blog' },
        { name: { en: 'Case Studies', es: 'Case Studies', pt: 'Case Studies' }, link: '/case-studies' },
        { name: { en: 'FAQs', es: 'FAQs', pt: 'FAQs' }, link: '/faq' },
        { name: { en: 'Comparison Hub', es: 'ComparisonHub', pt: 'ComparisonHub' }, link: '/compare' },
      ],
    },
    {
      name: { en: 'Enterprise', es: 'Empresa', pt: 'Empresa' },
      type: 'dropdown',
      items: [
        {
          name: {
            en: 'Our Core Technology',
            es: 'Nuestra tecnología principal',
            pt: 'Nossa tecnologia principal',
          },
          link: '/our-core-technology',
        },
        {
          name: {
            en: 'Workflow',
            es: 'Nuestra tecnología principal',
            pt: 'Fluxo de trabalho',
          },
          link: '/workflow',
        },
      ],
    },
    {
      name: { en: 'Pricing', es: 'Precios', pt: 'Preços' },
      title: 'Pricing',
      type: 'link',
      link: '/pricing',
    },
    { 'name': {en: 'About Us', es: "Sobre nosotros"}, title: 'About InspektLabs', 'type': 'link', 'link': '/about-us'},
    // {
    //   name: { en: 'en', es: 'es', pt: 'pt' },
    //   type: 'dropdown',
    //   items: [
    //     { name: { en: 'en', es: 'en', pt: 'en' }, link: '/lang' },
    //     { name: { en: 'es', es: 'es', pt: 'es' }, link: '/lang' },
    //     { name: { en: 'pt', es: 'pt', pt: 'pt' }, link: '/lang' },
    //   ],
    // },
    {
      name: {
        en: 'Contact sales',
        es: 'Contacta con nosotros',
        pt: 'Fale conosco',
      },
      type: 'button',
      link: '/contact-us',
      bgColor: '#1F1F1F',
      disablePopup: true, // always go to the /contact-us page, never the popup
    },
    {
      name: {
        en: 'Request a Demo',
        es: 'Solicitar una demostración',
        pt: 'Solicite uma demonstração',
      },
      type: 'button',
      link: '/contact-us',
      bgColor: '#195DFF',
    },
  ];

  const changeLanguage = () => {
    window.location.href = `https://inspektlabs.com${asPath}`;
  };

  const generateMenu = () => {
    return navMenu.map((menu, index) => {
      if (menu.type === 'dropdown')
        return (
          <li key={index} className='menu-items'>
            <div className='dropdown'>
              <div
                className={`dropbtn ${
                  menu.items
                    .map((subItem) => subItem.link)
                    .includes(router.asPath)
                    ? 'nav-active highlighted'
                    : ''
                }`}
              >
                {menu.name[locale]} 
              </div>
              <div className='dropdown-content'>
                {menu.items.map((subItem, subIndex) =>
                  subItem.link === '/blog' ? (
                    <a
                      key={`${index}-${subIndex}`}
                      title={subItem.title? subItem.title: 'Page link'}
                      href='https://inspektlabs.com/blog/'
                    >
                      {subItem.name[locale]}
                    </a>
                  ) : subItem.link === '/lang' ? (
                    <p
                      key={`${index}-${subIndex}`}
                      onClick={() => changeLanguage(subItem.name[locale])}
                      className='dropdown-item'
                      style={{ cursor: 'pointer', padding: '10px' }}
                    >
                      {subItem.name[locale]}
                    </p>
                  ) : (
                    <Link
                      key={`${index}-${subIndex}`}
                      href={subItem.link}
                      className='dropdown-item'
                      title={subItem.title? subItem.title: 'Page link'}
                      prefetch={false}
                    >
                      {subItem.name[locale]}
                    </Link>
                  )
                )}
              </div>
            </div>
          </li>
        );
      else if (menu.type === 'link')
        return (
          <li
            key={index}
            className={`menu-items ${
              menu.link === router.asPath ? 'nav-active' : ''
            }`}
          >
            <Link href={menu.link} prefetch={false} title={menu.title? menu.title: 'Page link'}>
              {menu.name[locale]}
            </Link>
          </li>
        );
      else if (menu.type === 'button')
        return (
          <li key={index} className='menu-items btn-container'>
            <ContactUsCTA as="div" cluster = "navbar" location="navbar" disablePopup={menu.disablePopup}>
              <a href={menu.link} className='btn-nav' style={{backgroundColor: menu.bgColor, marginLeft: menu.marginLeft? menu.marginLeft: ''}}>{menu.name[locale]}</a>
            </ContactUsCTA>
          </li>
        );
    });
  };

  const generateMobileMenu = () => {
    return navMenu.map((menu, index) => {
      if (menu.type === 'dropdown')
        return menu.items.map((subItem, subIndex) =>
          subItem.link === '/blog' ? (
            <a
              key={`${index}-${subIndex}`}
              href='https://inspektlabs.com/blog/'
              title={subItem.title? subItem.title: 'Page link'}
            >
              {subItem.name[locale]}
            </a>
          ) : subItem.link === '/lang' ? (
            <p
              key={`${index}-${subIndex}`}
              onClick={() => changeLanguage(subItem.name[locale])}
            >
              {subItem.name[locale]}
            </p>
          ) : (
            <Link key={`${index}-${subIndex}`} href={subItem.link} title={subItem.title? subItem.title: 'Page link'} prefetch={false}>
              <a
                className={`${
                  subItem.link === router.asPath ? 'nav-active mobile' : ''
                }`}
              >
                {subItem.name[locale]}
              </a>
            </Link>
          )
        );
      else
        return (
          <Link key={index} href={menu.link} title={menu.title? menu.title: 'Page link'}s prefetch={false}>
            <a
              className={`${
                menu.link === router.asPath ? 'nav-active mobile' : ''
              }`}
            >
              {menu.name[locale]}
            </a>
          </Link>
        );
    });
  };

  const openNav = () => (sideNav.current.style.transform = 'translateX(0)');

  const closeNav = () => (sideNav.current.style.transform = 'translateX(-100%)');

  useEffect(() => (sideNav.current.style.transform = 'translateX(-100%)'), [router]);

  return (
    <Fragment>
      {/* Mobile Menu */}
      <div ref={sideNav} id='sidenav' className='sidenav'>
        <div className='closebtn' onClick={closeNav}>
          &times;
        </div>
        <div className='logo-sidenav'>
          <Link href='/' prefetch={false}>
            <a>
              <Image src={logo} alt='Inspektlabs' />
            </a>
          </Link>
        </div>
        {generateMobileMenu()}
      </div>

      {/* Desktop Menu */}
      <nav className='nav'>
        <div className='m-menu-icon'>
          <Image src={menu} alt='Menu' onClick={openNav} />
        </div>
        <div className='logo'>
          <Link href='/' prefetch={false}>
            <a>
              <Image src={logo} alt='Inspektlabs'/>
            </a>
          </Link>
        </div>
        <ul className='menu m-hide'>{generateMenu()}</ul>
      </nav>
    </Fragment>
  );
};

export default Navbar;
